import type { ComboboxOption } from "@sentricmusic/ui";
import type { ColumnDef, Row } from "@tanstack/react-table";
import type { ChangeEvent } from "react";

import { Badge, Button, ButtonGroupSeparator, Combobox, Dialog, DialogContent, FormInput, IconBin, Table, TableActionsDropdown, TableExpandChevron } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DetailedSong } from "@/api/queries";

import { useDeleteSong } from "@/api/mutations";
import { useGetMyWriters } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import LoadingSmall from "@/components/LoadingSmall";
import { sendEvent } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";

import DeleteWorkModal from "./DeleteWorkModal";
import RecordingForm from "./RecordingForm";
import SongDetails from "./SongDetails";
import SongsCreateUpdate from "./SongsCreateUpdate";
import useSongCreationStore from "./store/songCreationStore";

type Props = {
  data: DetailedSong[];
  highlightColour: string;
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  adminRights: boolean;
};

// getmyworksdetailed's writersplits entries — narrower than (and not
// interchangeable with) the writersplits shape returned by useGetMySongsDetailed.
type SongTableWriterSplit = {
  writercode: string;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  isdeleted?: boolean;
};

function SongsTable({ data, highlightColour, total, isLoading, isFetching, adminRights }: Props) {
  const { t } = useTranslation(["songs", "common"]);

  const { data: siteConfig } = useSiteConfigAuth();
  const isCookingVinyl = siteConfig?.display_name?.includes("Cooking Vinyl Publishing") ?? false;

  const selectedAccount = useAppStore(state => state.selectedAccount) as string;
  const tableState = useAppStore(state => state.songsTable);
  const updateSongsTable = useAppStore(state => state.updateSongsTable);

  // Live input value — only copied into the committed `globalFilter` (which
  // drives the query) on Search/Enter/Clear, never on keystroke.
  const [searchTerm, setSearchTerm] = useState(tableState.globalFilter ?? "");

  useEffect(() => {
    setSearchTerm(tableState.globalFilter ?? "");
  }, [tableState.globalFilter]);

  // Same commit-on-Search pattern as searchTerm/globalFilter above.
  const [selectedWriterCode, setSelectedWriterCode] = useState<string | undefined>(tableState.writerCode ?? undefined);

  useEffect(() => {
    setSelectedWriterCode(tableState.writerCode ?? undefined);
  }, [tableState.writerCode]);

  const writers = useGetMyWriters({ aac: selectedAccount });

  const writerOptions = useMemo<ComboboxOption[]>(() => (
    (writers.data?.writers ?? []).map(w => ({
      value: w.writercode,
      label: [w.firstname, w.middlename, w.lastname].filter(Boolean).join(" "),
    }))
  ), [writers.data]);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [deletingWork, setDeletingWork] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<Row<DetailedSong> | null>(null);
  const [editingRow, setEditingRow] = useState<Row<DetailedSong> | null>(null);
  const [addingRecordingRow, setAddingRecordingRow] = useState<Row<DetailedSong> | null>(null);
  const { clearState: clearSongCreationState } = useSongCreationStore();

  // Clear the song-creation store shortly after the edit modal closes, so a
  // subsequent edit of a different song doesn't start from stale form state.
  useEffect(() => {
    if (editingRow === null) {
      const timer = setTimeout(() => {
        clearSongCreationState();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editingRow, clearSongCreationState]);

  const deleteWork = useDeleteSong();
  const queryClient = useQueryClient();

  const handleWarningClose = useCallback((): void => {
    setIsWarningModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleDelete = useCallback((rowToDelete: Row<DetailedSong> | null): void => {
    if (!rowToDelete)
      return;

    const { workcode, artistaccountcode } = rowToDelete.original;
    setDeletingWork(workcode);

    deleteWork.mutate(
      {
        artistaccountcode,
        workcode,
        isdeleted: true,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ["getMySongsDetailedPaginated"], exact: false });
          void queryClient.invalidateQueries({ queryKey: ["getSong"], exact: false });
          void queryClient.invalidateQueries({ queryKey: ["getRecordingsDetails"], exact: false });
          sendEvent({ property: "pe14489857_user_song_deletion" });
          setDeletingWork(null);
          handleWarningClose();
        },
        onError: (error: Error) => {
          setDeletingWork(null);
          handleWarningClose();
          // eslint-disable-next-line no-alert
          window.alert(`Failed to delete work ${workcode}: ${error.message}`);
        },
      },
    );
  }, [deleteWork, queryClient, handleWarningClose]);

  const handleOpenWarningModal = useCallback((row: Row<DetailedSong>): void => {
    setSelectedRow(row);
    const showWarning = localStorage.getItem("showDeleteWorkWarning");

    if (showWarning === "false") {
      handleDelete(row);
    }
    else {
      setIsWarningModalOpen(true);
    }
  }, [handleDelete]);

  const handleSearch = (): void => {
    updateSongsTable({
      globalFilter: searchTerm.trim() === "" ? null : searchTerm.trim(),
      writerCode: selectedWriterCode ?? null,
      page: 1,
    });
  };

  const handleClear = (): void => {
    setSearchTerm("");
    setSelectedWriterCode(undefined);
    updateSongsTable({ globalFilter: null, writerCode: null, page: 1 });
  };

  const columns = useMemo<ColumnDef<DetailedSong>[]>(() => {
    const createdDateColumn: ColumnDef<DetailedSong> = {
      id: "createddate",
      accessorKey: "createddate",
      header: String(t("songs:song-table-date")),
      size: 150,
      minSize: 120,
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value && value !== "0000-00-00" ? format(Date.parse(value), "yyyy-MM-dd") : null;
      },
    };

    return [
      {
        id: "title",
        accessorKey: "title",
        header: String(t("songs:song-table-name")),
        size: 220,
        minSize: 160,
        cell: ({ row }) => (
          <span className="flex items-center gap-2.5">
            <TableExpandChevron isExpanded={row.getIsExpanded()} onToggle={row.getToggleExpandedHandler()} />
            {row.original.title}
          </span>
        ),
      },
      {
        id: "performer",
        accessorFn: row => (row.performingartists ?? [])
          .filter(p => !p.isdeleted)
          .map(p => p.performingartistname)
          .join(", "),
        header: String(t("songs:song-table-performers")),
        size: 200,
        minSize: 150,
        enableSorting: false,
      },
      ...(isCookingVinyl ? [] : [createdDateColumn]),
      {
        id: "writers",
        accessorFn: row => ((row.writersplits as SongTableWriterSplit[] | undefined) ?? [])
          .filter(w => !w.isdeleted)
          .map(w => [w.firstname, w.middlename, w.lastname].filter(Boolean).join(" "))
          .join(", "),
        header: String(t("songs:song-table-writers")),
        size: 240,
        minSize: 180,
        enableSorting: false,
      },
      {
        id: "verifiedstate",
        accessorKey: "verifiedstate",
        header: String(t("songs:song-table-status")),
        size: 150,
        minSize: 120,
        enableSorting: true,
        cell: ({ row }) => {
          const { verifiedstate, changespending, isdeactivated, islocked } = row.original;

          if (isdeactivated) {
            return <Badge variant="danger">{String(t("common:pill-deactivated"))}</Badge>;
          }
          if (islocked) {
            return <Badge variant="warning">{String(t("common:pill-change"))}</Badge>;
          }
          if (verifiedstate !== 0 && changespending) {
            return <Badge variant="warning">{String(t("common:pill-change"))}</Badge>;
          }
          if (verifiedstate === 0) {
            return <Badge variant="warning">{String(t("common:pill-submitted"))}</Badge>;
          }
          if (verifiedstate === 1) {
            return <Badge variant="success">{String(t("common:pill-processed"))}</Badge>;
          }
          if (verifiedstate === 2) {
            return <Badge variant="danger">{String(t("common:pill-rejected"))}</Badge>;
          }
          return <Badge variant="warning">{String(t("common:pill-submitted"))}</Badge>;
        },
      },
      {
        id: "actions",
        header: "",
        size: 60,
        minSize: 60,
        maxSize: 60,
        enableSorting: false,
        enablePinning: true,
        meta: { pinSide: "right" },
        cell: ({ row }) => {
          const { verifiedstate, changespending, isdeactivated, workcode } = row.original;
          const canDelete = verifiedstate === 0 && changespending && !isdeactivated;
          const isDeleting = workcode === deletingWork;

          if (isDeleting) {
            return <LoadingSmall />;
          }

          return (
            <TableActionsDropdown>
              <Button
                variant="dropdown"
                label={String(t("songs:edit-song-link"))}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  if (adminRights) {
                    setEditingRow(row);
                  }
                }}
              />
              <Button
                variant="dropdown"
                label={String(t("songs:add-new-recording-link"))}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  if (adminRights) {
                    setAddingRecordingRow(row);
                  }
                }}
              />
              {canDelete && (
                <>
                  <ButtonGroupSeparator />
                  <Button
                    variant="dropdownDelete"
                    label={String(t("songs:delete-song-tooltip", "Delete song"))}
                    icon={<IconBin size="md" className="stroke-current" />}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      handleOpenWarningModal(row);
                    }}
                  />
                </>
              )}
            </TableActionsDropdown>
          );
        },
      },
    ];
  }, [t, isCookingVinyl, deletingWork, adminRights, handleOpenWarningModal]);

  const filterContent = (
    <>
      <FormInput
        label={String(t("common:search", "Search"))}
        smallFont
        placeholder={String(t("songs:song-search-placeholder", "Search by title or performing artist..."))}
        value={searchTerm}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
      />
      <Combobox
        label={String(t("songs:filter-writer-label", "Writer"))}
        smallFont
        data={writerOptions}
        value={selectedWriterCode}
        onValueChange={value => setSelectedWriterCode(value)}
        clearButton
        disabled={writers.isLoading}
        placeholder={writers.isLoading ? String(t("common:loading", "Loading...")) : String(t("songs:filter-writer-placeholder", "All writers"))}
        searchPlaceholder={String(t("songs:filter-writer-search-placeholder", "Search writers..."))}
      />
    </>
  );

  return (
    <>
      <DeleteWorkModal
        isOpen={isWarningModalOpen}
        onConfirm={() => handleDelete(selectedRow)}
        onClose={handleWarningClose}
      />

      <Dialog open={editingRow !== null} onOpenChange={open => !open && setEditingRow(null)}>
        <DialogContent className="max-w-6xl">
          {editingRow !== null && (
            <SongsCreateUpdate
              aac={editingRow.original.artistaccountcode}
              id={editingRow.original.workcode}
              closeForm={() => setEditingRow(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addingRecordingRow !== null} onOpenChange={open => !open && setAddingRecordingRow(null)}>
        <DialogContent className="max-w-3xl">
          {addingRecordingRow !== null && (
            <RecordingForm
              aac={addingRecordingRow.original.artistaccountcode}
              id={addingRecordingRow.original.workcode}
              closeForm={() => setAddingRecordingRow(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Table
        tableId="songs-table"
        data={data}
        columns={columns}
        highlightColour={highlightColour}
        getRowId={row => row.workcode}
        getRowCanExpand={() => true}
        hasExpandableRows
        renderSubComponent={({ row }: { row: Row<DetailedSong> }) => <SongDetails row={row} />}
        filterContent={filterContent}
        onSearch={handleSearch}
        onClear={handleClear}
        searchLabel={String(t("common:search", "Search"))}
        clearLabel={String(t("other:reset", "Clear"))}
        fetchingLabel={String(t("songs:fetching-track", "Fetching..."))}
        noResultsTitle={String(t("songs:no-search-results-title", "No results found"))}
        noResultsDescription={String(t("songs:no-search-results-description", "No records match the currently applied filters."))}
        noResultsClearLabel={String(t("songs:no-search-results-clear-button", "Clear filters"))}
        noDataAvailableLabel={String(t("songs:no-songs-available", "No songs available"))}
        hasActiveFilters={(tableState.globalFilter ?? "") !== "" || tableState.writerCode != null}
        sorting={tableState.sortBy ?? []}
        onSortingChange={sorting => updateSongsTable({ sortBy: sorting, page: 1 })}
        manualSorting
        pagination={{ pageIndex: tableState.page - 1, pageSize: tableState.perPage }}
        onPaginationChange={pagination => updateSongsTable({ page: pagination.pageIndex + 1, perPage: pagination.pageSize })}
        pageCount={Math.max(1, Math.ceil(total / tableState.perPage))}
        isLoading={isLoading}
        isRefetchingData={isFetching}
      />
    </>
  );
}

export default SongsTable;
